import {defineStore} from 'pinia';
import {ref} from 'vue';
import {useCookie, useNuxtApp} from '#app';
import {useItemStore} from '~/store/item';
import type {Creditor, User} from '~~/api/models';

export const useCompanyStore = defineStore('company', () => {
  // Composable
  const nuxtApp = useNuxtApp();
  const itemStore = useItemStore();

  // State
  const users = ref<User[]>([]);
  const allUsersLoaded = ref(false); // This is a fix for a refactor. Optimally we should only fetch the users we need.
  const cachedCreditors = ref<{ [key: string]: Creditor[] }>({});
  const creditorsAvailable = ref<{ [key: string]: number }>({});
  const currentPage = ref<{ [key: string]: number }>({});
  const cachedCompanyUsers = ref<{ [key: string]: User[] }>({});

  // Actions
  const cleanCompanyStore = () => {
    resetStore();
    useCookie('company').value = null;
  };

  const getUsers = async (userIds: string[] | null = null, force: boolean = false) => {
    // If no userIds are provided, fetch all users from the API if not already cached
    if (!userIds) {
      if (users.value.length > 0 && allUsersLoaded.value && !force) return users.value;

      users.value = [];

      let response = await nuxtApp.$apiService.Company.getUsers(null, 1, 1000);
      users.value.push(...response.data);

      while (response.hasNextPage) {
        response = await nuxtApp.$apiService.Company.getUsers(null, response.page + 1, 1000);
        users.value.push(...response.data);
      }

      allUsersLoaded.value = true;
      return users.value;
    }

    // Find the missing user IDs (not already in the cached users array)
    const missingIds = userIds.filter(
      (id) => !users.value.some((user) => user.id === id)
    );
    // If there are missing IDs, fetch them from the API
    if (missingIds.length > 0) {
      let response = await nuxtApp.$apiService.Company.getUsers(missingIds, 1, 1000);
            users.value.push(...response.data);
            while (response.hasNextPage) {
                response = await nuxtApp.$apiService.Company.getUsers(missingIds, response.page + 1, 1000);
                users.value.push(...response.data);// Add the newly fetched users to the cached array
              }
    }

    // Return all users corresponding to the requested userIds
    return users.value;
  };


  const getUser = (userId: string): User | undefined => {
    const user = users.value.find((user) => user.id === userId);

    if (user) return user;

    nuxtApp.$apiService.Company.getUser(userId).then((user) => {
      if (user) {
        // We can assume, since the request was called, that the user doesn't exist in the users array.
        users.value.push(user);
        return user;
      }
    }).catch((error) => {
      console.error(error);
      throw new Error('User not found');
    });

    return;
  }

  const getCompanyUsers = async (page: number = 1, pageSize: number = 50, search: string = ''): Promise<User[]> => {
    const tenantId = nuxtApp.$apiService.activeTenant as string;

    if (search === '') {
      if (cachedCompanyUsers.value[tenantId]?.length >= pageSize) {
        return cachedCompanyUsers.value[tenantId];
      }
    }

    const users = await nuxtApp.$apiService.Company.getPagedCompanyUsers(tenantId, page, pageSize, search, 'id,displayName,email');
    if (users.data?.length) {
      // Set the cachedCompanyUsers for the tenantId to the users.data. Distinct by the user.id.
      cachedCompanyUsers.value[tenantId] = users.data.filter((user, index, self) => index === self.findIndex(t => t.id === user.id));
      return cachedCompanyUsers.value[tenantId];
    }

    return [];
  }

  const getCreditors = async (pageSize: number = 50, search: string = ''): Promise<Creditor[]> => {
    const tenantId = itemStore.activeTenant as string;

    const cachedCreditorsForTenant = getCachedCreditors(tenantId);
    const cachedCreditorLength = cachedCreditorsForTenant.length;
    const availableCreditors = getCreditorsAvailable(tenantId);
    const currentPageForTenant = currentPage.value[tenantId] || 1;

    if (cachedCreditorLength >= availableCreditors && availableCreditors > 0) {
      return cachedCreditorsForTenant;
    }

    const page = search === '' ? currentPageForTenant : 1;
    const creditorsResponse = await nuxtApp.$apiService.Company.getCreditors(page, pageSize, search);

    if (search === '' && !creditorsAvailable.value[tenantId]) {
      setCreditorsAvailable(tenantId, creditorsResponse?.totalCount as number);
    }

    setCachedCreditors(tenantId, creditorsResponse?.data as Creditor[]);

    if (creditorsResponse?.hasNextPage && search === '') {
      currentPage.value[tenantId] = currentPageForTenant + 1;
    }

    return getCachedCreditors(tenantId);
  };

  const setCachedCreditors = (tenantId: string, creditors: Creditor[]) => {
    if (!cachedCreditors.value[tenantId]) {
      cachedCreditors.value[tenantId] = [];
    }

    creditors.forEach((creditor) => {
      const index = cachedCreditors.value[tenantId].findIndex((cachedCreditor) => cachedCreditor.id === creditor.id);

      if (index > -1) {
        cachedCreditors.value[tenantId][index] = creditor;
      } else {
        cachedCreditors.value[tenantId].push(creditor);
      }
    });

    return cachedCreditors.value[tenantId];
  };

  const getCachedCreditors = (tenantId: string) => {
    return cachedCreditors.value[tenantId] || [];
  };

  const setCreditorsAvailable = (tenantId: string, creditorsAvailableCount: number) => {
    creditorsAvailable.value[tenantId] = creditorsAvailableCount;
  };

  const getCreditorsAvailable = (tenantId: string) => {
    return creditorsAvailable.value[tenantId] || 0;
  };

  // Reset store
  const resetStore = () => {
    users.value = [];
    allUsersLoaded.value = false;
    cachedCreditors.value = {};
    creditorsAvailable.value = {};
    currentPage.value = {};
  };

  return {
    // State
    users,
    allUsersLoaded,
    cachedCreditors,
    creditorsAvailable,
    currentPage,

    // Getters
    getUsers,
    getUser,

    // Actions
    cleanCompanyStore,
    getCreditors,
    setCachedCreditors,
    getCachedCreditors,
    setCreditorsAvailable,
    getCreditorsAvailable,
    getCompanyUsers
  };
}, {
  persist: {
    pick: ['users', 'cachedCreditors', 'creditorsAvailable', 'currentPage', 'allUsersLoaded'],
    key: 'company'
  }
});